import { useEffect, useState } from "react";
import PageHeader from "../../components/PageHeader";
import Button from "../../components/Button";
import FormField from "../../components/FormField";
import TextInput from "../../components/TextInput";
import Badge from "../../components/Badge";
import { useHarvestConfig } from "../../../hooks/useHarvestConfig";
import { useSaveHarvestConfig } from "../../../hooks/useSaveHarvestConfig";
import { useDeleteHarvestConfig } from "../../../hooks/useDeleteHarvestConfig";
import { useRunHarvest } from "../../../hooks/useRunHarvest";

export default function HarvestPage() {
  const config = useHarvestConfig();
  const save = useSaveHarvestConfig();
  const remove = useDeleteHarvestConfig();
  const run = useRunHarvest();

  const [url, setUrl] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [importAsPaid, setImportAsPaid] = useState(false);

  useEffect(() => {
    if (config.data) {
      setUrl(config.data.sourceUrl);
      setEnabled(config.data.enabled);
      setImportAsPaid(config.data.importAsPaid);
    }
  }, [config.data]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    save.mutate({ sourceUrl: url.trim(), enabled, importAsPaid });
  };

  const hasConfig = !!config.data;

  return (
    <div>
      <PageHeader
        title="Moissonnage automatique"
        description="Renseignez l'URL d'une plateforme distante — un cron interne va récupérer ses parcours tous les matins à 06h00 et créer/mettre à jour les parcours locaux automatiquement."
      />

      <form onSubmit={onSubmit} className="bo-card bo-card--narrow bo-stack">
        <FormField
          label="URL de la plateforme"
          required
          hint="Ex. https://artist.dev.kolibri.be/api  (le schéma https:// est ajouté automatiquement si absent)"
        >
          <TextInput
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="artist.dev.kolibri.be"
            required
          />
        </FormField>

        <label className="bo-checkbox-row">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          <span>Activer le moissonnage automatique quotidien (06h00)</span>
        </label>

        <label className="bo-checkbox-row">
          <input
            type="checkbox"
            checked={importAsPaid}
            onChange={(e) => setImportAsPaid(e.target.checked)}
          />
          <span>Marquer les parcours importés comme payants</span>
        </label>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "var(--sp-2)" }}>
          {hasConfig && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => window.confirm("Supprimer la configuration de moissonnage ?") && remove.mutate()}
              loading={remove.isPending}
            >
              Désactiver
            </Button>
          )}
          <Button type="submit" variant="primary" loading={save.isPending}>
            {hasConfig ? "Enregistrer" : "Configurer la source"}
          </Button>
        </div>

        {save.isSuccess && <p className="bo-success-text">Configuration enregistrée.</p>}
        {save.isError && (
          <p className="bo-error-text">Erreur : {(save.error as Error)?.message}</p>
        )}
      </form>

      {hasConfig && (
        <>
          <div className="bo-card bo-card--narrow" style={{ marginTop: "var(--sp-5)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
              <div>
                <h3 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: 18 }}>Dernier passage</h3>
                <div style={{ fontSize: 12, color: "var(--ink-mute)", marginTop: 4 }}>
                  {config.data?.lastRunAt
                    ? new Date(config.data.lastRunAt).toLocaleString("fr-BE")
                    : "Jamais exécuté."}
                </div>
              </div>
              <Badge tone={config.data?.enabled ? "success" : "neutral"}>
                {config.data?.enabled ? "Cron actif" : "Cron désactivé"}
              </Badge>
            </div>
            {config.data?.lastRunSummary && (
              <p style={{ fontSize: 13, color: "var(--ink-soft)", marginTop: "var(--sp-3)" }}>
                {config.data.lastRunSummary}
              </p>
            )}
          </div>

          <div className="bo-card bo-card--narrow" style={{ marginTop: "var(--sp-5)" }}>
            <h3 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: 18 }}>
              Lancer maintenant
            </h3>
            <p style={{ fontSize: 13, color: "var(--ink-mute)", marginTop: "var(--sp-2)" }}>
              Utilisez ce bouton pour tester la connexion à la source ou déclencher un import sans attendre 06h00.
            </p>
            <div style={{ display: "flex", gap: "var(--sp-2)", marginTop: "var(--sp-3)" }}>
              <Button
                type="button"
                variant="secondary"
                onClick={() => run.mutate(true)}
                loading={run.isPending && run.variables === true}
              >
                Tester (preview)
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={() => run.mutate(false)}
                loading={run.isPending && run.variables === false}
              >
                Importer maintenant
              </Button>
            </div>
            {run.data && (
              <div style={{ marginTop: "var(--sp-4)", fontSize: 13, color: "var(--ink-soft)" }}>
                <strong style={{ color: "var(--ink)" }}>
                  {run.data.dryRun ? "Preview" : "Import"} terminé :
                </strong>{" "}
                {run.data.circuitsProcessed} circuits traités · {run.data.parcoursCreated} créés · {run.data.parcoursUpdated} mis à jour · {run.data.lieuxCreated + run.data.lieuxUpdated} lieux · {run.data.artistsCreated} artistes ajoutés
                {run.data.errors.length > 0 && (
                  <ul style={{ marginTop: "var(--sp-3)", color: "var(--red)", paddingLeft: 18, fontSize: 12 }}>
                    {run.data.errors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {run.isError && (
              <p className="bo-error-text">Erreur : {(run.error as Error)?.message}</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
